import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MenuItemCard } from '../components/restaurant/MenuItemCard';
import { ProductDetailModal } from '../components/restaurant/ProductDetailModal';
import { CartItem as CartItemComponent } from '../components/restaurant/CartItem';
import { useRestaurantBySlug, useMenuData } from '../lib/hooks';
import { useCart } from '../lib/cart';
import { supabase } from '../lib/supabase';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Store, ShoppingBag, X, MessageCircle, ArrowLeft, CheckCircle2, MapPin, Bike, Flame, Plus } from 'lucide-react';
import { MenuItem, CartItemOption } from '../lib/types';
import { formatCurrency, cn } from '../lib/utils';
import { buildWhatsAppMessage, buildWhatsAppUrl } from '../lib/whatsapp';
import { getSubdomainSlug } from '../lib/subdomain';
// Keep emoji helper for upsell section labels
function getCategoryEmoji(name: string): string {
  const emojiMap: Record<string, string> = {
    bebida: '🥤',
    postre: '🍮',
    taco: '🌮',
    burrito: '🌯',
    combo: '🔥',
    entrada: '🍽️',
    ensalada: '🥗',
    nacho: '🧀',
    sopa: '🍲',
    pizza: '🍕',
    hamburguesa: '🍔',
    extra: '✨',
    especial: '⭐'
  };
  const lower = name.toLowerCase().trim();
  for (const [key, emoji] of Object.entries(emojiMap)) {
    if (lower.includes(key)) return emoji;
  }
  return '🔥';
}
const SECTION_TITLES: Record<string, string> = {
  tacos: '🌮 Tacos que no fallan',
  burritos: '🌯 Burritos bien cargados',
  quesadillas: '🧀 Quesadillas irresistibles',
  bowls: '🥗 Bowls con power',
  antojitos: '🔥 Antojitos para compartir',
  hamburguesas: '🍔 Hamburguesas de verdad',
  pizzas: '🍕 Pizzas artesanales',
  combos: '💣 Combos para romper dieta',
  bebidas: '🥤 Para bajar el fuego',
  postres: '🍮 El toque dulce',
  entradas: '🍽️ Para abrir boca',
  ensaladas: '🥗 Algo más fresquito',
  extras: '✨ Extras que suman',
  especiales: '⭐ Especiales de la casa',
  nachos: '🧀 Nachos cargados',
  sopas: '🍲 Calditos reconfortantes'
};
function getCreativeTitle(name: string): string {
  const lower = name.toLowerCase().trim();
  for (const [key, title] of Object.entries(SECTION_TITLES)) {
    if (lower.includes(key)) return title;
  }
  return `🔥 ${name}`;
}
// Check if a category is an upsell category (bebidas/postres)
function isUpsellCategory(name: string): boolean {
  const lower = name.toLowerCase().trim();
  return ['bebida', 'bebidas', 'drinks', 'postre', 'postres', 'desserts', 'refresco', 'refrescos'].some(k => lower.includes(k));
}
interface PublicMenuPageProps {
  /** Optional slug from subdomain — overrides useParams */
  subdomainSlug?: string;
}
export function PublicMenuPage({
  subdomainSlug
}: PublicMenuPageProps = {}) {
  const {
    slug: pathSlug
  } = useParams<{
    slug: string;
  }>();
  // Priority: explicit subdomain prop > auto-detected subdomain > path param
  const slug = subdomainSlug || getSubdomainSlug() || pathSlug;
  const navigate = useNavigate();
  const {
    restaurant,
    loading: restaurantLoading
  } = useRestaurantBySlug(slug);
  const {
    categories,
    items,
    loading: menuLoading
  } = useMenuData(restaurant?.id);
  const {
    cart,
    addItem,
    updateQuantity,
    removeItem,
    getTotal,
    getItemCount,
    clearCart
  } = useCart();
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [animKey, setAnimKey] = useState(0); // for triggering re-animation
  // Checkout state
  const [cartStep, setCartStep] = useState<'review' | 'checkout' | 'success'>('review');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [deliveryType, setDeliveryType] = useState<'pickup' | 'delivery'>('pickup');
  const [orderNotes, setOrderNotes] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryLat, setDeliveryLat] = useState<number | null>(null);
  const [deliveryLng, setDeliveryLng] = useState<number | null>(null);
  const [mapsUrl, setMapsUrl] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [completedOrder, setCompletedOrder] = useState<{
    orderNumber: string;
    trackingToken: string;
    whatsappUrl: string;
  } | null>(null);
  // Set initial active category
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0].id);
    }
  }, [categories, activeCategory]);
  // Reset cart step when cart opens/closes
  useEffect(() => {
    if (!isCartOpen) {
      setCartStep('review');
      setSubmitError(null);
    }
  }, [isCartOpen]);
  const handleCategorySelect = (categoryId: string) => {
    setActiveCategory(categoryId);
    // Scroll to specific category section
    const el = document.getElementById(`category-${categoryId}`);
    if (el) {
      // Offset for sticky headers/nav
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  // Auto-update active category based on scroll position (lightweight observer)
  useEffect(() => {
    if (categories.length === 0) return;
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const id = entry.target.id.replace('category-', '');
          setActiveCategory(id);
          break;
        }
      }
    }, {
      rootMargin: '-80px 0px -70% 0px',
      threshold: 0
    });
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      categories.forEach(cat => {
        const el = document.getElementById(`category-${cat.id}`);
        if (el) observer.observe(el);
      });
    }, 100);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [categories]);
  const handleProductClick = (item: MenuItem) => {
    setSelectedProduct(item);
    setIsModalOpen(true);
  };
  const handleAddToCart = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    if (!restaurant) return;
    const success = addItem({
      menu_item_id: item.id,
      restaurant_id: restaurant.id,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity,
      notes,
      image_url: item.image_url,
      selected_options: options
    }, restaurant.name);
    if (!success) {
      alert('Ya tienes productos de otro restaurante en tu carrito. Por favor, finaliza o vacía tu pedido actual primero.');
    } else {
      setIsCartOpen(true);
    }
  };
  // Quick add for upsell (no options, quantity 1)
  const handleQuickAdd = (item: MenuItem) => {
    if (!restaurant) return;
    addItem({
      menu_item_id: item.id,
      restaurant_id: restaurant.id,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity: 1,
      notes: '',
      image_url: item.image_url,
      selected_options: []
    }, restaurant.name);
  };
  // Intercept checkout: show upsell first
  const handleProceedToCheckout = () => {
    // Check if there are upsell items available
    const upsellItems = getUpsellItems();
    if (upsellItems.length > 0) {
      setShowUpsellModal(true);
    } else {
      setCartStep('checkout');
    }
  };
  // Get upsell items (bebidas + postres)
  const getUpsellItems = () => {
    const upsellCats = categories.filter(c => isUpsellCategory(c.name));
    return items.filter(item => upsellCats.some(c => c.id === item.category_id));
  };
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Tu navegador no soporta geolocalización. Ingresa tu dirección manualmente.');
      return;
    }
    setIsLocating(true);
    setLocationError(null);
    navigator.geolocation.getCurrentPosition(position => {
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      setDeliveryLat(lat);
      setDeliveryLng(lng);
      setMapsUrl(`https://www.google.com/maps?q=${lat},${lng}`);
      setIsLocating(false);
      setLocationError(null);
      console.log('[NOIZA][public] Location captured:', {
        lat,
        lng
      });
    }, error => {
      setIsLocating(false);
      console.error('[NOIZA][public] Geolocation error:', error);
      switch (error.code) {
        case error.PERMISSION_DENIED:
          setLocationError('Permiso de ubicación denegado. Ingresa tu dirección manualmente.');
          break;
        case error.POSITION_UNAVAILABLE:
          setLocationError('No se pudo obtener tu ubicación. Ingresa tu dirección manualmente.');
          break;
        default:
          setLocationError('Error al obtener ubicación. Ingresa tu dirección manualmente.');
      }
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };
  const handleSubmitOrder = async () => {
    if (!restaurant || !cart.restaurantId) return;
    if (!customerName.trim() || !customerPhone.trim()) {
      setSubmitError('Por favor ingresa tu nombre y teléfono.');
      return;
    }
    if (deliveryType === 'delivery' && !mapsUrl && !deliveryAddress.trim()) {
      setSubmitError('Para delivery, comparte tu ubicación o ingresa tu dirección.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const subtotal = getTotal();
      const deliveryFee = deliveryType === 'delivery' ? restaurant.express_fee || 0 : 0;
      const orderTotal = subtotal + deliveryFee;
      const trackingToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      const orderPayload = {
        restaurant_id: cart.restaurantId,
        tracking_token: trackingToken,
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        delivery_type: deliveryType,
        payment_method: 'cash',
        delivery_address: deliveryType === 'delivery' ? deliveryAddress.trim() || null : null,
        delivery_lat: deliveryType === 'delivery' ? deliveryLat : null,
        delivery_lng: deliveryType === 'delivery' ? deliveryLng : null,
        maps_url: deliveryType === 'delivery' ? mapsUrl : null,
        table_id: null,
        notes: orderNotes.trim() || null,
        subtotal,
        delivery_fee: deliveryFee,
        total: orderTotal,
        status: 'new',
        source: 'public_web'
      };
      console.log('[NOIZA][public] Creating order:', JSON.stringify(orderPayload, null, 2));
      const {
        data: order,
        error: orderError
      } = await supabase.from('orders').insert(orderPayload).select().single();
      if (orderError) {
        console.error('[NOIZA][public] Order insert error:', orderError);
        throw new Error(`Error al crear pedido: ${orderError.message}`);
      }
      console.log('[NOIZA][public] Order created:', order);
      for (const item of cart.items) {
        const optionsTotal = item.selected_options.reduce((sum, opt) => sum + opt.price_delta, 0);
        const lineTotal = (item.unit_price_snapshot + optionsTotal) * item.quantity;
        const itemPayload = {
          order_id: order.id,
          restaurant_id: cart.restaurantId,
          menu_item_id: item.menu_item_id,
          item_name_snapshot: item.item_name_snapshot,
          unit_price_snapshot: item.unit_price_snapshot,
          quantity: item.quantity,
          notes: item.notes || null,
          station_code: null,
          line_total: lineTotal,
          status: 'pending'
        };
        console.log('[NOIZA][public] Creating order item:', JSON.stringify(itemPayload, null, 2));
        const {
          data: orderItem,
          error: itemError
        } = await supabase.from('order_items').insert(itemPayload).select().single();
        if (itemError) {
          console.error('[NOIZA][public] Order item insert error:', itemError, {
            item: item.item_name_snapshot
          });
          throw new Error(`Error al guardar producto: ${itemError.message}`);
        }
        console.log('[NOIZA][public] Order item created:', orderItem);
        if (item.selected_options.length > 0) {
          const optionsToInsert = item.selected_options.map(opt => ({
            order_item_id: orderItem.id,
            restaurant_id: cart.restaurantId,
            option_group: opt.option_group || '',
            option_name: opt.option_name,
            price_delta: opt.price_delta
          }));
          console.log('[NOIZA][public] Creating order item options:', JSON.stringify(optionsToInsert, null, 2));
          const {
            error: optionsError
          } = await supabase.from('order_item_options').insert(optionsToInsert);
          if (optionsError) {
            console.error('[NOIZA][public] Order item options insert error:', optionsError);
          }
        }
      }
      const {
        error: trackingError
      } = await supabase.from('customer_tracking_events').insert({
        restaurant_id: cart.restaurantId,
        order_id: order.id,
        status: 'new',
        title: 'Pedido recibido',
        message: 'Tu pedido fue recibido correctamente y está esperando confirmación del restaurante.',
        visible_to_customer: true
      });
      if (trackingError) {
        console.error('[NOIZA][public] Tracking event insert error:', trackingError);
      }
      const {
        error: logError
      } = await supabase.from('order_logs').insert({
        restaurant_id: cart.restaurantId,
        order_id: order.id,
        event_type: 'order_created',
        event_payload: {
          source: 'public_web',
          delivery_type: deliveryType
        }
      });
      if (logError) {
        console.error('[NOIZA][public] Order log insert error:', logError);
      }
      const itemsSnapshot = [...cart.items];
      let ticketImageUrl: string | null = null;
      try {
        console.log('[NOIZA][public] Ticket generation started...');
        const {
          generateTicketImage,
          uploadTicketToCloudinary
        } = await import('../lib/ticket');
        const ticketBlob = await generateTicketImage({
          restaurantName: restaurant.name,
          currencySymbol: restaurant.currency_symbol || '₡',
          orderNumber: order.order_number,
          customerName: customerName.trim(),
          customerPhone: customerPhone.trim(),
          deliveryType,
          deliveryAddress: deliveryType === 'delivery' ? deliveryAddress.trim() || null : null,
          mapsUrl: deliveryType === 'delivery' ? mapsUrl : null,
          items: itemsSnapshot,
          subtotal,
          deliveryFee,
          total: orderTotal,
          notes: orderNotes.trim() || null,
          createdAt: order.created_at || new Date().toISOString()
        });
        console.log('[NOIZA][public] Ticket blob generated, size:', ticketBlob.size);
        const url = await uploadTicketToCloudinary(ticketBlob, order.order_number);
        if (url) {
          ticketImageUrl = url;
          console.log('[NOIZA][public] Ticket uploaded:', ticketImageUrl);
          const {
            error: ticketUpdateError
          } = await supabase.from('orders').update({
            ticket_image_url: ticketImageUrl
          }).eq('id', order.id);
          if (ticketUpdateError) {
            console.error('[NOIZA][public] Ticket URL update error:', ticketUpdateError);
          } else {
            console.log('[NOIZA][public] Ticket URL saved to order row');
          }
        } else {
          console.warn('[NOIZA][public] Ticket upload returned null URL');
        }
      } catch (ticketErr) {
        console.error('[NOIZA][public] Ticket generation/upload failed:', ticketErr);
      }
      console.log('[NOIZA][public] Final ticket_image_url:', ticketImageUrl);
      const whatsappMessage = buildWhatsAppMessage({
        restaurantName: restaurant.name,
        whatsappNumber: restaurant.whatsapp_number || '',
        currencySymbol: restaurant.currency_symbol || '₡',
        orderNumber: order.order_number,
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        deliveryType,
        paymentMethod: 'cash',
        items: itemsSnapshot,
        subtotal,
        deliveryFee,
        total: orderTotal,
        deliveryAddress: deliveryType === 'delivery' ? deliveryAddress.trim() || null : null,
        mapsUrl: deliveryType === 'delivery' ? mapsUrl : null,
        notes: orderNotes.trim() || null,
        ticketImageUrl,
        trackingToken
      });
      console.log('[NOIZA][public] Final WhatsApp message payload:', {
        orderNumber: order.order_number,
        ticketImageUrl,
        trackingToken,
        itemCount: itemsSnapshot.length
      });
      const whatsappUrl = restaurant.whatsapp_number ? buildWhatsAppUrl(restaurant.whatsapp_number, whatsappMessage) : '';
      console.log('[NOIZA][public] WhatsApp URL:', whatsappUrl);
      setCompletedOrder({
        orderNumber: order.order_number,
        trackingToken,
        whatsappUrl
      });
      setCartStep('success');
      if (whatsappUrl) {
        window.open(whatsappUrl, '_blank');
      }
      clearCart();
    } catch (err: any) {
      console.error('[NOIZA][public] Order submission error:', err);
      setSubmitError(err?.message || 'Error al procesar tu pedido. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // ─── LOADING ───
  if (restaurantLoading || menuLoading) {
    return <div className="min-h-screen flex flex-col bg-[#0B0B0B]" data-id="element-1272">
        <div className="h-[60vh] sm:h-[50vh] w-full bg-[#141414] animate-pulse" data-id="element-1273" />
        <div className="max-w-4xl mx-auto px-4 w-full mt-8" data-id="element-1274">
          <Skeleton className="h-8 w-48 mb-6 bg-[#141414]" data-id="element-1275" />
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-8" data-id="element-1276">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="aspect-square rounded-2xl bg-[#141414]" data-id="element-1277" />)}
          </div>
          <div className="grid grid-cols-2 gap-3" data-id="element-1278">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="aspect-[4/5] rounded-2xl bg-[#141414]" data-id="element-1279" />)}
          </div>
        </div>
      </div>;
  }
  // ─── NOT FOUND ───
  if (!restaurant) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0B] p-4 text-[#F8F5F0]" data-id="element-1280">
        <EmptyState icon={Store} title="Restaurante no encontrado" description="El restaurante que buscas no existe o no está disponible." actionLabel="Volver al inicio" onAction={() => navigate('/')} data-id="element-1281" />
      </div>;
  }
  const cartCount = getItemCount();
  const cartTotal = getTotal();
  // Active category items
  const activeCategoryObj = categories.find(c => c.id === activeCategory);
  const activeCategoryItems = items.filter(item => item.category_id === activeCategory);
  // Upsell items
  const upsellItems = getUpsellItems();
  // Group upsell items by category for display
  const upsellGroups = categories.filter(c => isUpsellCategory(c.name)).map(c => ({
    category: c,
    items: items.filter(i => i.category_id === c.id)
  })).filter(g => g.items.length > 0);
  // Get featured items
  const featuredItems = items.filter((item: any) => item.featured === true || item.is_featured === true);
  // If not enough featured items, grab a few that have images
  const displayFeatured = featuredItems.length >= 3 ? featuredItems : [...featuredItems, ...items.filter(i => i.image_url && !featuredItems.find(f => f.id === i.id))].slice(0, 6);
  return <div className="min-h-screen flex flex-col bg-[#0B0B0B] pb-28 relative" data-id="element-1282">
      {/* ═══ HERO ═══ */}
      <div className="relative h-[40vh] sm:h-[45vh] w-full overflow-hidden bg-[#0B0B0B]" data-id="element-1283">
        {restaurant.cover_url ? <img src={restaurant.cover_url} alt="Cover" className="absolute inset-0 w-full h-full object-cover" loading="eager" data-id="element-1284" /> : <div className="absolute inset-0 w-full h-full" style={{
        background: 'linear-gradient(160deg, #006847 0%, #004d35 40%, #0B0B0B 100%)'
      }} data-id="element-1285" />}

        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/60 to-black/20" data-id="element-1286" />

        {/* Open/Closed Badge */}
        <div className="absolute top-4 right-4 z-10" data-id="element-1287">
          <div className="flex items-center gap-1.5 bg-black/70 px-3 py-1.5 rounded-full border border-white/10" data-id="element-1288">
            <div className={cn('w-2 h-2 rounded-full', restaurant.is_open ? 'bg-[#2ECC71]' : 'bg-[#CE1126]')} data-id="element-1289" />
            <span className="text-xs font-medium text-white" data-id="element-1290">
              {restaurant.is_open ? 'Abierto ahora' : 'Cerrado'}
            </span>
          </div>
        </div>

        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 max-w-4xl mx-auto flex flex-col items-center text-center pb-6 sm:pb-10" data-id="element-1291">
          {restaurant.logo_url && <img src={restaurant.logo_url} alt={restaurant.name} className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl object-cover border-2 border-[#F4D35E]/30 mb-3 sm:mb-4 bg-[#141414]" loading="eager" data-id="element-1292" />}
          <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tighter mb-1.5 sm:mb-2" data-id="element-1293">
            {restaurant.name}
          </h1>
          <p className="text-sm sm:text-lg text-white/70 font-medium mb-4 sm:mb-6 max-w-md px-4" data-id="element-1294">
            {restaurant.description_short || 'Sabor mexicano que se siente 🔥'}
          </p>
          <button onClick={() => {
          const el = document.getElementById('menu-start');
          if (el) {
            el.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }} className="bg-[#006847] text-white px-7 py-3 sm:px-8 sm:py-3.5 rounded-full text-sm sm:text-base font-bold shadow-lg active:scale-95 transition-transform" data-id="element-1295">
            <span className="flex items-center gap-2" data-id="element-1296">
              Hacé tu pedido
              <Flame className="w-5 h-5" data-id="element-1297" />
            </span>
          </button>
        </div>
      </div>

      {categories.length === 0 ? <div className="relative z-10 max-w-4xl mx-auto w-full px-4 py-12" data-id="element-1298">
          <EmptyState icon={Store} title="Menú no disponible" description="Este restaurante aún no ha configurado su menú." data-id="element-1299" />
        </div> : <div id="menu-start" className="relative z-10 max-w-6xl mx-auto w-full flex flex-col sm:flex-row items-start gap-6 px-0 sm:px-6 pt-0 sm:pt-8" data-id="element-1300">
          {/* ═══ MENU NAVIGATION ═══ */}
          {/* Mobile: Sticky horizontal pills */}
          <div className="sm:hidden sticky top-0 z-30 w-full bg-[#0B0B0B] border-b border-white/5 px-3 py-3 overflow-x-auto no-scrollbar flex items-center gap-2" data-id="element-1301">
            {categories.map(category => {
          const isActive = activeCategory === category.id;
          return <button key={category.id} onClick={() => handleCategorySelect(category.id)} className={cn('whitespace-nowrap px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 border', isActive ? 'bg-[#006847] text-white border-[#006847] shadow-lg shadow-[#006847]/20' : 'bg-[#141414] text-white/70 border-white/10 hover:border-white/20')} data-id="element-1302">
                  <span className="text-sm" data-id="element-1303">
                    {getCategoryEmoji(category.name)}
                  </span>
                  {category.name}
                </button>;
        })}
          </div>

          {/* Desktop: Sticky left sidebar */}
          <div className="hidden sm:flex sticky top-24 w-64 shrink-0 flex-col gap-1 pr-6 border-r border-white/5 max-h-[calc(100vh-8rem)] overflow-y-auto no-scrollbar" data-id="element-1304">
            <h3 className="text-xs font-black text-white/40 uppercase tracking-widest mb-3 px-3" data-id="element-1305">
              Menú
            </h3>
            {categories.map(category => {
          const isActive = activeCategory === category.id;
          return <button key={category.id} onClick={() => handleCategorySelect(category.id)} className={cn('w-full text-left px-3 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-3', isActive ? 'bg-[#006847]/10 text-[#F4D35E]' : 'text-white/70 hover:bg-white/5 hover:text-white')} data-id="element-1306">
                  <span className="text-lg" data-id="element-1307">
                    {getCategoryEmoji(category.name)}
                  </span>
                  {category.name}
                </button>;
        })}
          </div>

          {/* ═══ MAIN CONTENT AREA ═══ */}
          <div className="flex-1 w-full px-3 sm:px-0 pb-12" data-id="element-1308">
            {/* FEATURED SECTION */}
            {displayFeatured.length > 0 && <section className="mb-10 sm:mb-14 pt-4 sm:pt-0" data-id="element-1309">
                <div className="flex items-center gap-2 mb-4 sm:mb-6 px-1" data-id="element-1310">
                  <span className="text-2xl" data-id="element-1311">⭐</span>
                  <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight" data-id="element-1312">
                    Destacados de la casa
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-5" data-id="element-1313">
                  {displayFeatured.map((item: any) => <div key={`featured-${item.id}`} data-id="element-1314">
                      <MenuItemCard item={item} onClick={handleProductClick} data-id="element-1315" />
                    </div>)}
                </div>
              </section>}

            {/* FULL MENU BY CATEGORY */}
            <div className="flex flex-col gap-10 sm:gap-14" data-id="element-1316">
              {categories.map(category => {
            const categoryItems = items.filter(item => item.category_id === category.id);
            if (categoryItems.length === 0) return null;
            return <section key={category.id} id={`category-${category.id}`} className="scroll-mt-24 sm:scroll-mt-8" data-id="element-1317">
                    <div className="flex items-center justify-between mb-4 sm:mb-6 px-1 border-b border-white/5 pb-3" data-id="element-1318">
                      <h2 className="text-xl sm:text-2xl font-black text-[#F4D35E] tracking-tight" data-id="element-1319">
                        {getCreativeTitle(category.name)}
                      </h2>
                      <span className="text-xs font-bold text-white/30 bg-white/5 px-2.5 py-1 rounded-full" data-id="element-1320">
                        {categoryItems.length}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-5" data-id="element-1321">
                      {categoryItems.map((item, idx) => <div key={item.id} data-id="element-1322">
                          <MenuItemCard item={item} onClick={handleProductClick} data-id="element-1323" />
                        </div>)}
                    </div>
                  </section>;
          })}
            </div>
          </div>
        </div>}

      {/* ═══ FLOATING CART BUTTON ═══ */}
      {cartCount > 0 && !isCartOpen && <div className="fixed bottom-4 sm:bottom-6 left-0 right-0 z-40 px-3 sm:px-4 pointer-events-none" style={{
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }} data-id="element-1324">
          <div className="max-w-4xl mx-auto flex justify-center sm:justify-end" data-id="element-1325">
            <button onClick={() => setIsCartOpen(true)} className="pointer-events-auto flex items-center justify-between w-full sm:w-auto sm:min-w-[320px] h-12 sm:h-14 px-5 sm:px-6 rounded-full text-white bg-[#006847] shadow-lg active:scale-95 transition-transform" data-id="element-1326">
              <div className="flex items-center gap-3" data-id="element-1327">
                <div className="w-8 h-8 bg-[#F4D35E] text-[#0B0B0B] rounded-full flex items-center justify-center font-bold text-sm" data-id="element-1328">
                  {cartCount}
                </div>
                <span className="font-bold text-[15px]" data-id="element-1329">Ver pedido</span>
              </div>
              <span className="font-bold text-[15px]" data-id="element-1330">
                {formatCurrency(cartTotal)}
              </span>
            </button>
          </div>
        </div>}

      {/* ═══ UPSELL MODAL ═══ */}
      {showUpsellModal && <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-id="element-1331">
          <div className="fixed inset-0 bg-black/80" onClick={() => {
        setShowUpsellModal(false);
        setCartStep('checkout');
      }} data-id="element-1332" />
          <div className="relative w-full sm:max-w-lg max-h-[85vh] bg-[#0B0B0B] sm:rounded-2xl rounded-t-2xl shadow-lg flex flex-col anim-slide-up border border-white/10" data-id="element-1333">
            <div className="px-6 pt-6 pb-4 shrink-0" data-id="element-1334">
              <div className="flex items-center justify-between mb-1" data-id="element-1335">
                <h3 className="text-xl font-black text-white" data-id="element-1336">
                  ¿Le agregamos algo más? 🤤
                </h3>
                <button onClick={() => {
              setShowUpsellModal(false);
              setCartStep('checkout');
            }} className="p-1.5 text-white/40 hover:text-white transition-colors" data-id="element-1337">
                  <X className="w-5 h-5" data-id="element-1338" />
                </button>
              </div>
              <p className="text-sm text-white/50" data-id="element-1339">
                Complementa tu pedido con estas opciones
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-4" data-id="element-1340">
              {upsellGroups.map(group => <div key={group.category.id} className="mb-6 last:mb-2" data-id="element-1341">
                  <h4 className="text-sm font-bold text-[#F4D35E] mb-3 uppercase tracking-wider" data-id="element-1342">
                    {getCategoryEmoji(group.category.name)}{' '}
                    {group.category.name}
                  </h4>
                  <div className="space-y-2" data-id="element-1343">
                    {group.items.map(item => <div key={item.id} className="flex items-center gap-3 bg-[#141414] rounded-xl p-3 border border-white/5" data-id="element-1344">
                        {item.image_url ? <img src={item.image_url} alt={item.name} className="w-14 h-14 rounded-xl object-cover shrink-0" data-id="element-1345" /> : <div className="w-14 h-14 rounded-xl bg-[#1a1a1a] flex items-center justify-center shrink-0" data-id="element-1346">
                            <span className="text-xl opacity-30" data-id="element-1347">
                              {getCategoryEmoji(group.category.name)}
                            </span>
                          </div>}
                        <div className="flex-1 min-w-0" data-id="element-1348">
                          <p className="text-sm font-semibold text-white truncate" data-id="element-1349">
                            {item.name}
                          </p>
                          <p className="text-sm font-bold text-[#F4D35E]" data-id="element-1350">
                            {formatCurrency(item.price)}
                          </p>
                        </div>
                        <button onClick={() => handleQuickAdd(item)} className="w-9 h-9 rounded-full bg-[#006847] text-white flex items-center justify-center hover:bg-[#005a3d] transition-colors shadow-md shadow-[#006847]/20 shrink-0 active:scale-90" data-id="element-1351">
                          <Plus className="w-4 h-4" data-id="element-1352" />
                        </button>
                      </div>)}
                  </div>
                </div>)}
              {upsellGroups.length === 0 && <p className="text-center text-white/40 py-8 text-sm" data-id="element-1353">
                  No hay sugerencias disponibles
                </p>}
            </div>

            <div className="px-6 pb-6 pt-3 shrink-0 border-t border-white/5 space-y-2.5" data-id="element-1354">
              <button onClick={() => setShowUpsellModal(false)} className="w-full h-12 rounded-xl bg-[#141414] border border-white/10 text-white font-bold text-sm hover:bg-[#1a1a1a] transition-colors" data-id="element-1355">
                Seguir agregando
              </button>
              <button onClick={() => {
            setShowUpsellModal(false);
            setCartStep('checkout');
          }} className="w-full h-12 rounded-xl bg-[#006847] text-white font-bold text-sm shadow-lg shadow-[#006847]/30 hover:bg-[#005a3d] transition-colors" data-id="element-1356">
                Continuar al pedido →
              </button>
            </div>
          </div>
        </div>}

      {/* ═══ SLIDE-OVER CART PANEL ═══ */}
      {isCartOpen && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-1357">
          <div className="fixed inset-0 bg-black/80" onClick={() => setIsCartOpen(false)} data-id="element-1358" />
          <div className="relative w-full sm:w-[420px] max-w-full h-full bg-[#0B0B0B] shadow-lg flex flex-col anim-slide-right" data-id="element-1359">
            {/* Cart Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 bg-[#0B0B0B] shrink-0" data-id="element-1360">
              <div className="flex items-center gap-3" data-id="element-1361">
                {cartStep === 'checkout' ? <button onClick={() => setCartStep('review')} className="p-1.5 -ml-1.5 text-white/60 hover:text-white rounded-full hover:bg-white/5 transition-colors" data-id="element-1362">
                    <ArrowLeft className="w-5 h-5" data-id="element-1363" />
                  </button> : <div className="w-10 h-10 rounded-full bg-[#006847]/10 border border-[#006847]/20 flex items-center justify-center" data-id="element-1364">
                    <ShoppingBag className="w-5 h-5 text-[#006847]" data-id="element-1365" />
                  </div>}
                <div data-id="element-1366">
                  <h2 className="text-lg font-bold text-white leading-tight" data-id="element-1367">
                    {cartStep === 'review' && 'Tu Pedido 🔥'}
                    {cartStep === 'checkout' && 'Datos de contacto'}
                    {cartStep === 'success' && '¡Pedido enviado!'}
                  </h2>
                  <p className="text-xs font-medium text-white/50" data-id="element-1368">
                    {restaurant.name}
                  </p>
                </div>
              </div>
              <button onClick={() => setIsCartOpen(false)} className="p-2 -mr-2 text-white/60 hover:text-white rounded-full hover:bg-white/5 transition-colors" data-id="element-1369">
                <X className="w-5 h-5" data-id="element-1370" />
              </button>
            </div>

            {/* Step: Review Cart */}
            {cartStep === 'review' && <>
                <div className="flex-1 overflow-y-auto px-6 py-2" data-id="element-1371">
                  {cart.items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center space-y-4" data-id="element-1372">
                      <div className="w-20 h-20 bg-[#141414] rounded-full flex items-center justify-center" data-id="element-1373">
                        <span className="text-4xl" data-id="element-1374">🌮</span>
                      </div>
                      <div data-id="element-1375">
                        <p className="font-bold text-lg text-white" data-id="element-1376">
                          Tu carrito está vacío
                        </p>
                        <p className="text-sm text-white/50 mt-1 max-w-[250px]" data-id="element-1377">
                          Agrega deliciosos productos del menú para empezar tu
                          pedido.
                        </p>
                      </div>
                      <button onClick={() => setIsCartOpen(false)} className="mt-4 px-6 py-2 bg-[#006847] text-white rounded-full font-bold hover:bg-[#005a3d] transition-colors" data-id="element-1378">
                        Explorar menú
                      </button>
                    </div> : <div className="divide-y divide-white/5" data-id="element-1379">
                      {cart.items.map(item => <CartItemComponent key={item.id} item={item} onUpdateQuantity={updateQuantity} onRemove={removeItem} data-id="element-1380" />)}
                    </div>}
                </div>

                {cart.items.length > 0 && <div className="border-t border-white/10 bg-[#141414] p-6 space-y-4 shrink-0" data-id="element-1381">
                    <div className="flex justify-between items-center" data-id="element-1382">
                      <span className="text-white/70 font-medium" data-id="element-1383">Total</span>
                      <span className="text-2xl font-black text-[#F4D35E]" data-id="element-1384">
                        {formatCurrency(cartTotal)}
                      </span>
                    </div>
                    <button onClick={handleProceedToCheckout} className="w-full bg-[#006847] text-white h-14 rounded-xl text-[15px] font-bold shadow-lg active:bg-[#005a3d] transition-colors" data-id="element-1385">
                      Continuar al Checkout
                    </button>
                    <button onClick={() => {
              if (confirm('¿Estás seguro de vaciar el carrito?')) clearCart();
            }} className="w-full text-center text-sm font-medium text-white/30 hover:text-[#CE1126] transition-colors py-1" data-id="element-1386">
                      Vaciar carrito
                    </button>
                  </div>}
              </>}

            {/* Step: Checkout */}
            {cartStep === 'checkout' && <>
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6" data-id="element-1387">
                  <div className="space-y-4" data-id="element-1388">
                    <div className="space-y-1.5" data-id="element-1389">
                      <label className="block text-sm font-semibold text-white" data-id="element-1390">
                        Tu nombre
                      </label>
                      <input type="text" placeholder="Ej: María López" value={customerName} onChange={e => setCustomerName(e.target.value)} required className="w-full px-4 py-3 rounded-xl bg-[#141414] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#006847] focus:border-transparent" data-id="element-1391" />
                    </div>
                    <div className="space-y-1.5" data-id="element-1392">
                      <label className="block text-sm font-semibold text-white" data-id="element-1393">
                        Tu teléfono
                      </label>
                      <input type="tel" placeholder="Ej: 8888-8888" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} required className="w-full px-4 py-3 rounded-xl bg-[#141414] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#006847] focus:border-transparent" data-id="element-1394" />
                    </div>
                  </div>

                  <div data-id="element-1395">
                    <label className="block text-sm font-semibold text-white mb-3" data-id="element-1396">
                      Tipo de entrega
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-1397">
                      <button type="button" onClick={() => setDeliveryType('pickup')} className={cn('p-4 rounded-xl border-2 text-sm font-semibold transition-all flex flex-col items-center justify-center gap-2', deliveryType === 'pickup' ? 'border-[#006847] bg-[#006847] text-white shadow-md shadow-[#006847]/20' : 'border-white/10 bg-[#141414] text-white/60 hover:border-white/20')} data-id="element-1398">
                        <Store className="w-5 h-5" data-id="element-1399" />
                        Para llevar
                      </button>
                      <button type="button" onClick={() => setDeliveryType('delivery')} className={cn('p-4 rounded-xl border-2 text-sm font-semibold transition-all flex flex-col items-center justify-center gap-2', deliveryType === 'delivery' ? 'border-[#006847] bg-[#006847] text-white shadow-md shadow-[#006847]/20' : 'border-white/10 bg-[#141414] text-white/60 hover:border-white/20')} data-id="element-1400">
                        <Bike className="w-5 h-5" data-id="element-1401" />
                        Delivery
                      </button>
                    </div>
                  </div>

                  {deliveryType === 'delivery' && <div className="space-y-4 bg-[#141414] p-4 rounded-xl border border-white/10" data-id="element-1402">
                      <label className="block text-sm font-semibold text-white" data-id="element-1403">
                        Ubicación de entrega
                      </label>
                      <button type="button" onClick={handleGetLocation} disabled={isLocating} className={cn('w-full flex items-center justify-center gap-2 p-3.5 rounded-xl border-2 text-sm font-semibold transition-all', mapsUrl ? 'border-[#006847]/30 bg-[#006847]/10 text-[#2ECC71]' : 'border-white/10 bg-[#141414] text-white hover:border-white/20 shadow-sm')} data-id="element-1404">
                        {isLocating ? <>
                            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" data-id="element-1405">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1406" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-1407" />
                            </svg>
                            Obteniendo ubicación...
                          </> : mapsUrl ? <>
                            <CheckCircle2 className="w-5 h-5" data-id="element-1408" />
                            Ubicación capturada
                          </> : <>
                            <MapPin className="w-5 h-5" data-id="element-1409" />
                            Compartir mi ubicación
                          </>}
                      </button>
                      {mapsUrl && <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="block text-xs text-[#F4D35E] underline underline-offset-2 truncate px-1" data-id="element-1410">
                          {mapsUrl}
                        </a>}
                      {locationError && <p className="text-sm text-[#CE1126] bg-[#CE1126]/10 border border-[#CE1126]/20 rounded-lg px-3 py-2.5" data-id="element-1411">
                          {locationError}
                        </p>}
                      <div className="pt-2 space-y-1.5" data-id="element-1412">
                        <label className="block text-sm font-semibold text-white" data-id="element-1413">
                          Dirección exacta
                        </label>
                        <input type="text" placeholder="Ej: 200m norte del parque central, casa azul" value={deliveryAddress} onChange={e => setDeliveryAddress(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-[#141414] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#006847] focus:border-transparent" data-id="element-1414" />
                      </div>
                    </div>}

                  <div data-id="element-1415">
                    <label className="block text-sm font-semibold text-white mb-2" data-id="element-1416">
                      Notas (opcional)
                    </label>
                    <textarea value={orderNotes} onChange={e => setOrderNotes(e.target.value)} placeholder="Instrucciones especiales..." rows={3} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-[#141414] text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#006847] focus:border-transparent resize-none" data-id="element-1417" />
                  </div>

                  {/* Order Summary */}
                  <div className="bg-[#141414] rounded-xl p-5 space-y-3 text-sm border border-white/10" data-id="element-1418">
                    <h4 className="font-bold text-white mb-3 text-base" data-id="element-1419">
                      Resumen del pedido
                    </h4>
                    {cart.items.map(item => {
                const optTotal = item.selected_options.reduce((s, o) => s + o.price_delta, 0);
                const lineTotal = (item.unit_price_snapshot + optTotal) * item.quantity;
                return <div key={item.id} className="flex justify-between text-white/70" data-id="element-1420">
                          <span className="pr-4" data-id="element-1421">
                            <span className="font-semibold text-white" data-id="element-1422">
                              {item.quantity}x
                            </span>{' '}
                            {item.item_name_snapshot}
                          </span>
                          <span className="font-medium text-white shrink-0" data-id="element-1423">
                            {formatCurrency(lineTotal)}
                          </span>
                        </div>;
              })}
                    {deliveryType === 'delivery' && restaurant.express_fee > 0 && <div className="flex justify-between text-white/70 pt-3 border-t border-white/10 mt-3" data-id="element-1424">
                          <span data-id="element-1425">Envío</span>
                          <span className="font-medium text-white" data-id="element-1426">
                            {formatCurrency(restaurant.express_fee)}
                          </span>
                        </div>}
                    <div className="flex justify-between font-bold text-white pt-3 border-t border-white/10 text-base mt-3" data-id="element-1427">
                      <span data-id="element-1428">Total</span>
                      <span className="text-[#F4D35E]" data-id="element-1429">
                        {formatCurrency(cartTotal + (deliveryType === 'delivery' ? restaurant.express_fee || 0 : 0))}
                      </span>
                    </div>
                  </div>

                  {submitError && <div className="bg-[#CE1126]/10 border border-[#CE1126]/20 text-[#CE1126] text-sm font-medium rounded-xl px-4 py-3 flex items-start gap-2" data-id="element-1430">
                      <X className="w-5 h-5 shrink-0" data-id="element-1431" />
                      <p data-id="element-1432">{submitError}</p>
                    </div>}
                </div>

                <div className="border-t border-white/10 bg-[#0B0B0B] p-6 shrink-0" data-id="element-1433">
                  <button onClick={handleSubmitOrder} disabled={isSubmitting || !customerName.trim() || !customerPhone.trim()} className={cn('w-full h-14 rounded-xl text-[15px] font-bold flex items-center justify-center transition-all', isSubmitting || !customerName.trim() || !customerPhone.trim() ? 'bg-[#141414] text-white/30 cursor-not-allowed' : 'bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-lg shadow-[#25D366]/30')} data-id="element-1434">
                    {isSubmitting ? <span className="flex items-center" data-id="element-1435">
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" data-id="element-1436">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1437" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-1438" />
                        </svg>
                        Procesando...
                      </span> : <>
                        <MessageCircle className="w-5 h-5 mr-2" data-id="element-1439" />
                        Confirmar y enviar por WhatsApp
                      </>}
                  </button>
                </div>
              </>}

            {/* Step: Success */}
            {cartStep === 'success' && completedOrder && <div className="flex-1 flex flex-col items-center justify-center text-center px-8 space-y-8" data-id="element-1440">
                <div className="relative" data-id="element-1441">
                  <div className="absolute inset-0 bg-[#006847] blur-3xl opacity-20 rounded-full scale-150" data-id="element-1442" />
                  <div className="w-28 h-28 bg-[#006847]/10 rounded-full flex items-center justify-center relative border-4 border-[#006847]/30" data-id="element-1443">
                    <CheckCircle2 className="w-14 h-14 text-[#2ECC71]" data-id="element-1444" />
                  </div>
                </div>

                <div data-id="element-1445">
                  <h3 className="text-3xl font-black text-white mb-2" data-id="element-1446">
                    ¡Pedido Confirmado! 🎉
                  </h3>
                  <p className="text-white/60 text-base mb-4" data-id="element-1447">
                    Tu pedido{' '}
                    <span className="font-bold text-[#F4D35E] text-lg" data-id="element-1448">
                      #{completedOrder.orderNumber}
                    </span>{' '}
                    ha sido registrado y enviado al restaurante.
                  </p>
                </div>

                <div className="w-full space-y-3" data-id="element-1449">
                  {completedOrder.whatsappUrl && <button onClick={() => window.open(completedOrder.whatsappUrl, '_blank')} className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-xl h-14 font-bold shadow-md flex items-center justify-center transition-colors" data-id="element-1450">
                      <MessageCircle className="w-5 h-5 mr-2" data-id="element-1451" />
                      Abrir WhatsApp de nuevo
                    </button>}
                  <button onClick={() => window.location.href = `/track/${completedOrder.trackingToken}`} className="w-full bg-[#141414] border border-white/10 text-white hover:bg-[#1a1a1a] rounded-xl h-14 font-bold transition-colors" data-id="element-1452">
                    Seguir mi pedido
                  </button>
                </div>

                <button onClick={() => {
            setIsCartOpen(false);
            setCartStep('review');
            setCompletedOrder(null);
            setCustomerName('');
            setCustomerPhone('');
            setOrderNotes('');
          }} className="text-sm font-medium text-white/40 hover:text-white transition-colors" data-id="element-1453">
                  Volver al menú
                </button>
              </div>}
          </div>
        </div>}

      <ProductDetailModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} item={selectedProduct} onAdd={handleAddToCart} data-id="element-1454" />
    </div>;
}